import React, { useState, useEffect } from 'react';
import { TextField, Button, Container, Typography, Paper, Box, Link, CircularProgress, Alert, Snackbar, FormControlLabel, Checkbox } from '@mui/material';
import { useFetch } from '../../utils/FetchTemplate';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../../contexts/LoadingContext';
import { IUser, LoginResponse } from '../../interfaces/User.interface';

import { Role } from '../../interfaces/constants';

const SignIn: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { loggedIn, login, setSource } = useAuth(); 
  const [msg, setMsg] = useState('');
  const [is2FA, set2FA] = useState(false);
  const navigate = useNavigate();
  const { isLoading } = useLoading();
  const { typedPostData, getData, postDataResponse } = useFetch()
  const [twoFACode, setTwoFACode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [googleAuthorized, setGoogleAuthorized] = useState(false);

  useEffect(() => {
    if (loggedIn && googleAuthorized) {
      navigate('/dashboard');
    }
  }, [loggedIn, googleAuthorized, navigate]);

  const handleSignIn = async () => {
    try {
      setSource("own");
      const user = await typedPostData<IUser, LoginResponse>('/api/auth/signin', { email, password });
  
      if (user.message === "2FA code sent") {
        set2FA(true);
        return;
      }
  
      if (user.subscription) {
        user.subscription.subscriptionExpireDate = new Date(user.subscription.subscriptionExpireDate);
      }

      // 学生：不需要 Google，直接放行
      if (user.role === Role.Student || user.role === Role.Teacher) {
        login(user);
        setGoogleAuthorized(true);
        return;
      }
      // admin：需要 Google 授权
      login(user);
      if (user.role === Role.Admin) {
        const data = await getData<{ googleAuthorized: boolean }>('/api/booking/session');
        setGoogleAuthorized(data.googleAuthorized)

        if (!data.googleAuthorized) {
          const popup = window.open(
            `${process.env.REACT_APP_BACKEND_URL}/api/booking/oauth2auth`,
            "GoogleAuth",
            "width=600,height=650"
          );

          if (!popup) {
            setMsg("Allow window pop up for Google Authorization");
            return;
          }

          const onMessage = (event: MessageEvent) => {
            if (event.origin !== process.env.REACT_APP_BACKEND_URL) return;
            if (event.data?.type === "oauth" && event.data.success) {
              window.removeEventListener("message", onMessage);
              if (!popup.closed) popup.close();
              setGoogleAuthorized(true);  
            }
          };
          window.addEventListener("message", onMessage);
          return;
        }
      } 
    } catch (error) {
      setMsg("Error signing in. Please check your email and password.");
    }
  };
  

  const handleSubmit2FA = async () => {
    postDataResponse('/api/auth/verify-2fa', { email, password, twoFactorCode: twoFACode, rememberMe }).then((res) => {
      if (res.status != 200){
        res.json().then(data => setMsg(data.message));
        return;
      }
      res.json().then(data => {
        login(data);
        // Small delay to ensure session is established before navigation
        setTimeout(() => {
          navigate('/dashboard');
        }, 100);
      });
    }).catch(error => {
    }).finally(() => {
    });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      handleSignIn();
    }
  };

  const handleForgotPassword = async () => {
    if (newPassword !== confirmPassword) {
      setMsg("Passwords do not match");
      return;
    }

    typedPostData<any, any>('/api/auth/forgot-password', { 
      email, 
      password: newPassword 
    }).then(() => {
      setMsg("Reset password confirmation has been sent to your email");
      setIsForgotPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    }).catch(error => {
      setMsg("Error resetting password. Please try again.");
    });
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ padding: 4, mt: 8 }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <img src="/logo.png" alt="Logo" style={{ width: '150px', marginBottom: '20px' }} />
          {!is2FA && !isForgotPassword && <>
          <Typography variant="h4" gutterBottom>Sign In</Typography>
          <TextField
            label="Email"
            type="email"
            value={email}
            fullWidth
            margin="normal"
            variant="outlined"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyPress}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            fullWidth
            margin="normal"
            variant="outlined"
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyPress}
          />
          <Box width="100%" sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
            <Link
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setIsForgotPassword(true);
              }}
              sx={{ fontSize: '0.875rem' }}
            >
              Forgot Password?
            </Link>
          </Box>
          <Box mt={2} width="100%">
            <Button onClick={handleSignIn}
              variant="contained"
              color="primary"
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </Box>

          {/* <Box mt={2} width="100%">
            <Button
              onClick={() => setSource("passport")}
              variant="outlined"
              color="primary"
              fullWidth
              href={process.env.REACT_APP_BACKEND_URL + "/api/auth/google"}
              sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <img src="third-party signin/google/web_light_rd_na.svg" alt="Google logo" style={{ width: 24, height: 24, marginRight: 8 }} />
              Sign in with Google
            </Button>
            <Button
              onClick={() => setSource("passport")}
              variant="outlined"
              color="primary"
              fullWidth
              href={process.env.REACT_APP_BACKEND_URL + "/api/auth/facebook"}
              sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <img src="\third-party signin\facebook\Primary Logo\Facebook_Logo_Primary.png" alt="Facebook logo" style={{ width: 24, height: 24, marginRight: 8 }} />
              Sign in with Facebook
            </Button>
          </Box> */}
          {/* <Box mt={2}>
            <Typography variant="body2">
              Do not have an account? <Link href="/signup">Sign Up</Link>
            </Typography>
          </Box> */}
          </>}
          
          {!is2FA && isForgotPassword && <>
            <Typography variant="h4" gutterBottom>Reset Password</Typography>
            <TextField
              label="Email"
              type="email"
              value={email}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              label="New Password"
              type="password"
              value={newPassword}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <TextField
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              fullWidth
              margin="normal"
              variant="outlined"
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <Box mt={2} width="100%">
              <Button
                onClick={handleForgotPassword}
                variant="contained"
                color="primary"
                fullWidth
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Reset Password'}
              </Button>
            </Box>
            <Box mt={2}>
              <Typography variant="body2">
                <Link href="#" onClick={(e) => {
                  e.preventDefault();
                  setIsForgotPassword(false);
                }}>Back to Sign In</Link>
              </Typography>
            </Box>
          </>}

          {is2FA && <Box mt={2} width="100%">
            <TextField
              label="2FA Code"
              variant="outlined"
              fullWidth
              onChange={(e) => setTwoFACode(e.target.value)}
            />
            <Box mt={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    color="primary"
                  />
                }
                label={<Typography variant="body2">Remember me for 7 days</Typography>}
              />
            </Box>
            <Box mt={1}>
              <Button
                onClick={handleSubmit2FA}
                variant="contained"
                color="primary"
                fullWidth
              >
                Submit
              </Button>
            </Box>
          </Box>}
        </Box>
      </Paper>
      <Snackbar open={msg != ''} autoHideDuration={5000} onClose={() => setMsg('')}>
        <Alert onClose={() => setMsg('')} severity={msg.includes("confirmation has been sent") ? "success" : "error"} sx={{ width: '100%' }}>
          {msg}
        </Alert>
      </Snackbar>
    
    </Container>
  );
};

export default SignIn;
